import { useEffect } from 'react';

import { useCustomTestEdit } from 'pages/custom_tests/components/custom_test_edit/context';
import { CustomTestApi, CustomTestRequiredLicense } from 'apis/CustomTestApi';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { Col, Row } from 'react-bootstrap';
import { useSuperState } from 'hooks';
import { useApp } from 'app';

export const DetailsTab = () => {

    const { name, assignmentRefreshKey, categoryId, createdDate = '', languageId = 0, languages = [], minScore, minutes, modeId, owner = '',
        productIds, questionOrderMode, randomly, testId, totalQuestions = 0, useRandomly } = useCustomTestEdit();
    const { formatMessage } = useIntl();
    const { selectedPartner } = useApp();

    const [state, setState] = useSuperState({
        assignmentCount: 0,
        loading: true,
        programNames: [] as string[],
        requiredLicenses: [] as CustomTestRequiredLicense[]
    });
    const { assignmentCount, loading, programNames, requiredLicenses } = state;

    const loadData = async () => {

        try {

            setState({ loading: true });

            const [products, assignedInfo, requiredLicenses] = await Promise.all([
                categoryId > 0 ? CustomTestApi.GetProductsByCategory(categoryId, selectedPartner.id) : Promise.resolve([]),
                CustomTestApi.GetAssignedTransactionsAndGroups(testId, selectedPartner.id),
                CustomTestApi.GetRequiredLicenses(testId)
            ]);

            const programNames = (products ?? [])
                .filter(product => productIds.includes(product.Id))
                .map(product => product.Name);

            const assignmentCount = (assignedInfo?.AssignedTransactionIds?.length ?? 0)
                + (assignedInfo?.AssignedStudentGroupIds?.length ?? 0);

            setState({ assignmentCount, loading: false, programNames, requiredLicenses: requiredLicenses ?? [] });

        } catch (e) {

            setState({ loading: false });

        }

    };

    useEffect(() => {

        if (testId > 0) loadData();

    }, [testId, assignmentRefreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const getModeLabel = (id: number) => id === 1
        ? (formatMessage({ id: 'training.1fce7301', defaultMessage: 'Training' }))
        : id === 3
            ? (formatMessage({ id: 'both.8074bfd4', defaultMessage: 'Both' }))
            : (formatMessage({ id: 'testing.a439953c', defaultMessage: 'Testing' }));

    const deliveryText = useRandomly && randomly > 0
        ? formatMessage({ id: 'random_subset_delivered_of_total_questio.5a3b9d00', defaultMessage: 'Random subset - {delivered} of {total} questions' }, { delivered: randomly, total: totalQuestions })
        : formatMessage({ id: 'all_total_questions.5bc007fb', defaultMessage: 'All {total} questions' }, { total: totalQuestions });

    const orderText = questionOrderMode === 2
        ? (formatMessage({ id: 'curriculum_order.2958c1d3', defaultMessage: 'Curriculum order' }))
        : (formatMessage({ id: 'fully_randomized.3abbb070', defaultMessage: 'Fully randomized' }));

    const languageName = languages.find(language => language.Id === languageId)?.Name ?? '';
    const showLanguage = Boolean(languageName) || languageId > 0;
    const languageText = languageName || `${formatMessage({ id: 'language.96f7d270', defaultMessage: 'Language #' })}${languageId}`;

    const renderDetail = (label: string, value: React.ReactNode) => (
        <Col className={'mb-3'} md={4} sm={12}>
            <div className={'small text-muted'}>
                {label}
            </div>
            <div className={'fw-semibold'}>
                {value}
            </div>
        </Col>
    );

    const renderSetting = (label: string, value: React.ReactNode, last = false) => (
        <div className={`d-flex align-items-start gap-3 py-2 ${last ? '' : 'border-bottom'}`}>
            <span className={'text-muted text-nowrap'} style={{ minWidth: '9rem' }}>
                {label}
            </span>
            <span className={'fw-semibold'}>
                {value}
            </span>
        </div>
    );

    // Each license renders as a label/value row matching the Test Settings grid style; the
    // per-program grouping header only appears when the test spans multiple programs.
    const renderProgramLicenses = (program: CustomTestRequiredLicense, showHeader: boolean) => (
        <div key={program.ProductId}>
            {showHeader && (
                <div className={'fw-semibold small pt-2'}>
                    {program.ProductName}
                </div>
            )}
            {program.Licenses.length ?
                program.Licenses.map((license, index) => (
                    <div key={license.Id}>
                        {renderSetting(formatMessage({ id: 'license.506a29aa', defaultMessage: 'License' }), license.Name, index === program.Licenses.length - 1)}
                    </div>
                ))
                :
                (
                    <div className={'small text-warning py-2'}>
                        <FormattedMessage defaultMessage="No active license grants this program" id="no_active_license_grants_this_program.bf40acae" />
                    </div>
                )}
        </div>
    );

    return (
        <div className={'details-tab d-flex flex-column gap-4'}>

            {loading ?
                (
                    <div className={'d-flex align-items-center justify-content-center p-5'}>
                        <GMetrixLoader size={'large'} />
                    </div>
                )
                :
                (
                    <>
                        <section>
                            <h6 className={'fw-bold text-primary mb-3'}>
                                <FormattedMessage defaultMessage="Test Details" id="test_details.88f73a4e" />
                            </h6>
                            <Row>
                                {renderDetail(formatMessage({ id: 'test_name.77ea159f', defaultMessage: 'Test Name' }), name || '—')}
                                {renderDetail(
                                    formatMessage({ id: 'programs.b8b8156b', defaultMessage: 'Programs' }),
                                    programNames.length ?
                                        (
                                            <div>
                                                {programNames.map(programName => (
                                                    <div key={programName}>
                                                        {programName}
                                                    </div>
                                                ))}
                                            </div>
                                        )
                                        : '—'
                                )}
                                {renderDetail(formatMessage({ id: 'created_by.07f4fa41', defaultMessage: 'Created by' }), owner || '—')}
                                {renderDetail(
                                    formatMessage({ id: 'created_date.b2cdf64f', defaultMessage: 'Created Date' }),
                                    createdDate ? <FormattedDate value={new Date(createdDate)} /> : '—'
                                )}
                                {renderDetail(

                                    // This is the "Questions:" field label, not the lowercase suffix below.
                                    formatMessage({ id: 'questions.940c8cce', defaultMessage: 'Questions' }),
                                    `${totalQuestions} ${(formatMessage({ id: 'questions.04b685ee', defaultMessage: 'questions' })).toLowerCase()}`
                                )}
                                {renderDetail(
                                    formatMessage({ id: 'assignments.f2dfa1bf', defaultMessage: 'Assignments' }),
                                    assignmentCount > 0
                                        ? `${assignmentCount} ${assignmentCount === 1 ? (formatMessage({ id: 'assignment.b0883ba8', defaultMessage: 'assignment' })) : (formatMessage({ id: 'assignments.c50d9c2a', defaultMessage: 'assignments' }))}`
                                        : (formatMessage({ id: 'none.a51ad9ee', defaultMessage: 'None' }))
                                )}
                            </Row>
                        </section>

                        <section>
                            <h6 className={'fw-bold text-primary mb-3'}>
                                <FormattedMessage defaultMessage="Test Settings" id="test_settings.dfedf589" />
                            </h6>
                            <div className={'border rounded bg-light px-3 py-2'}>
                                <Row className={'gx-4'}>
                                    <Col md={6} xs={12}>
                                        {renderSetting(formatMessage({ id: 'passing_score.5fe03d6f', defaultMessage: 'Passing Score' }), `${minScore}%`)}
                                        {renderSetting(formatMessage({ id: 'time_limit.d7ee27f0', defaultMessage: 'Time limit' }), `${minutes} ${formatMessage({ id: 'mins.f79cb658', defaultMessage: 'mins' })}`)}
                                        {renderSetting(formatMessage({ id: 'testing_mode.3727f585', defaultMessage: 'Testing Mode' }), getModeLabel(modeId), true)}
                                    </Col>
                                    <Col md={6} xs={12}>
                                        {renderSetting(formatMessage({ id: 'question_delivery.579381e7', defaultMessage: 'Question delivery' }), deliveryText)}
                                        {renderSetting(formatMessage({ id: 'question_order.8f29c4fa', defaultMessage: 'Question order' }), orderText, !showLanguage)}
                                        {/* Question Language is an intentional extra beyond the mock — it surfaces real test data. */}
                                        {showLanguage && renderSetting(formatMessage({ id: 'question_language.dfd461a5', defaultMessage: 'Question Language' }), languageText, true)}
                                    </Col>
                                </Row>
                            </div>
                        </section>

                        <section>
                            <div className={'d-flex align-items-center gap-2 mb-3'}>
                                <h6 className={'fw-bold text-primary mb-0'}>
                                    <FormattedMessage defaultMessage="Required licenses for assignment" id="required_licenses_for_assignment.c8f6eb0e" />
                                </h6>
                                <i className={'fa-light fa-circle-info text-muted'} />
                            </div>
                            <div className={'bg-light rounded p-3 d-flex flex-column gap-2'}>
                                {requiredLicenses.length ?
                                    requiredLicenses.map(program => renderProgramLicenses(program, requiredLicenses.length > 1))
                                    :
                                    (
                                        <span className={'text-muted small'}>
                                            <FormattedMessage defaultMessage="Licensing requirements are validated when this test is assigned to a classroom or access code." id="licensing_requirements_are_validated_whe.a2cae110" />
                                        </span>
                                    )}
                            </div>
                        </section>
                    </>
                )}

        </div>
    );

};
